/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment.js library for date and time formatting.
import _ from 'underscore' // Npm: Underscore.js library for utility functions.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { useQuery } from '@apollo/client' // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * SIBLINGS
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import FallbackRouteDropDownOptions from 'components/FallbackRouteDropDownOptions'
import FallbackRouteUpsert from 'components/FallbackRouteUpsert'
import FallbackRouteDelete from 'components/FallbackRouteDelete'
// import RouteTerminal from 'components/RouteTerminal' // Disabled until route terminal is restored.


/*
 * GRAPHS
 */
import FallbackRouteReadQuery from './__query__/index.fallbackroute.read.query'


/*
 * STYLES
 */
import { buttonStyle, tabStyles } from './index.style'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _routes = _.without(Object.React.App.enums.CUSTOMER_TYPE.enums?.map(i => i.key), 'TERMINATION')
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({
    'items': [],
    'logicOperator': 'and',
    'quickFilterValues': [],
    'quickFilterLogicOperator': 'and'
  })
  const _setFilterDebounced = React.useMemo(() => _.debounce(setFilterModel, 500), [])
  const [sortModel, setSortModel] = React.useState([])
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [tabIndex, setTabIndex] = React.useState(0)

  React.useEffect(() => {
    return () => _setFilterDebounced.cancel?.()
  }, [_setFilterDebounced])

  const { 'isOpen': isRouteCreateFormOpen, 'onOpen': onRouteCreateFormOpen, 'onClose': onRouteCreateFormClose } = useDisclosure()
  const { 'isOpen': isRouteUpdateFormOpen, 'onOpen': onRouteUpdateFormOpen, 'onClose': onRouteUpdateFormClose } = useDisclosure()
  const { 'onOpen': onDeleteModalOpen, 'isOpen': isDeleteModalOpen, 'onClose': onDeleteModalClose } = useDisclosure()
  // const { 'isOpen': isRouteTerminalOpen, 'onOpen': onRouteTerminalOpen, 'onClose': onRouteTerminalClose } = useDisclosure()

  // Data assignment.
  const _isFilterApplied = filterModel?.items?.some(i => i.value != void 0 && i.value !== '')
  const _appliedFilter = _isFilterApplied ? filterModel : void 0
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(current => {
      const nextPaginationModel = { ...current, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])

  const _FallbackRouteReadVariables = React.useMemo(() => ({
    'type': _routes[tabIndex],
    'filter': _appliedFilter,
    'skip': paginationModel.page * paginationModel.pageSize,
    'take': paginationModel.pageSize
  }), [_appliedFilter, paginationModel.page, paginationModel.pageSize, _routes, tabIndex])

  const _FallbackRouteRead = useQuery(FallbackRouteReadQuery, {
    'variables': _FallbackRouteReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })

  const { refetch } = _FallbackRouteRead

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [refetch])

  const _isFirstLoadCompleted = React.useRef(false)
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': true, 'md': false }, { 'fallback': 'md' }) ?? false

  // Data assignment.
  if (!_FallbackRouteRead.loading && 0 < _FallbackRouteRead.data?.FallbackRouteRead?.length) _isFirstLoadCompleted.current = true
  if (0 === _FallbackRouteRead?.data?.FallbackRouteRead?.length || !_.every(_FallbackRouteRead?.data?.FallbackRouteRead, j => _successFlags.includes(j.status)) || _.some(_FallbackRouteRead?.data?.FallbackRouteRead, j => 'ROUTE_NOT_FOUND' === j.status)) _isFirstLoadCompleted.current = true
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    _setFilterDebounced(__filterModel)
  }, [_ResetPagination, _setFilterDebounced])
  const _HandleTabChange = React.useCallback(r => {
    _ResetPagination()
    setTabIndex(r)
  }, [_ResetPagination])

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': true,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'disableColumnMenu': true,
      'headerAlign': 'center'
    },
    {
      'field': 'displayName',
      'headerName': 'Route Name',
      'description': 'Name of the route.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'type',
      'headerName': 'Route Type',
      'description': 'Type of route.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      // 'disableColumnMenu': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 130
    },
    {
      'field': 'createdAt',
      'headerName': 'CreatedAt',
      'description': 'Creation date of route.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'updatedAt',
      'headerName': 'UpdatedAt',
      'description': 'Last update date of route.',
      'editable': false,
      'sortable': true,
      'filterable': true,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'createdBy',
      'headerName': 'Created By',
      'description': 'User who created the route.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'action',
      'headerName': 'Action',
      'description': 'Actions available for the route.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      renderCell: ({ row }) => (
        <FallbackRouteDropDownOptions
          type={row?.type}
          routeId={row?.id}
          customerAccountId={row?.customerAccountId}
          onRouteDelete={onDeleteModalOpen}
          onRouteUpdate={onRouteUpdateFormOpen}
          /* onRouteTerminalOpen={onRouteTerminalOpen} */
        />
      )
    }
  ], [])

  // Define rows
  const rows = React.useMemo(() => _.compact(
    _FallbackRouteRead?.data?.FallbackRouteRead?.map((r, index) =>
      _successFlags.includes(r.status) ? ({
        ...r,
        'id': r?.id ?? `fallback-route-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
        'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
        'displayName': r?.displayName || '-',
        'type': r?.type || '-',
        'createdAt': r?.createdAt ? Moment(r.createdAt).format('YYYY-MM-DD') : '-',
        'updatedAt': r?.updatedAt ? Moment(r.updatedAt).format('YYYY-MM-DD') : '-',
        'createdBy': r?.Account?.displayName || '-',
        'customerAccountId': r?.customerAccountId || r?.Account?.id || null
      }) : null
    )
  ), [_FallbackRouteRead?.data?.FallbackRouteRead, paginationModel.page, paginationModel.pageSize])
  const _FallbackRouteReadRowCount = _.first(_FallbackRouteRead?.data?.FallbackRouteRead)?._totalCount
  const rowCount = React.useMemo(() => {
    if (undefined !== _FallbackRouteReadRowCount && null !== _FallbackRouteReadRowCount) _rowCountRef.current = _FallbackRouteReadRowCount

    return _rowCountRef.current
  }, [_FallbackRouteReadRowCount])


  // Return component.
  return (
    <>
      <Flex
        className="route base"
        height="calc(100vh - 120px)"
        minH={0}
        maxH="calc(100vh - 120px)"
        width="100%"
        overflow="hidden"
      >
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
            minH="auto"
          >
            <Tooltip label="Create new route." fontSize="sm">
              <Button
                w="170px"
                onClick={onRouteCreateFormOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New Route
              </Button>
            </Tooltip>
            <Flex gap="12px">
              <Tooltip label="Refresh routes." fontSize="sm">
                <Button
                  borderRadius="8px"
                  fontWeight="450"
                  minW="20px"
                  backgroundColor="purple.200"
                  color="purple"
                  _hover={{ bg: "purple.800", color: "white" }}
                  leftIcon={<HiArrowPath />}
                  isDisabled={isRefreshing}
                  onClick={_HandleRefresh}
                >
                  Refresh
                </Button>
              </Tooltip>
              <DownloadToExcel
                cellsData={
                  0 === _FallbackRouteRead?.data?.FallbackRouteRead?.length ||
                  !_.every(
                    _.pluck(
                      _FallbackRouteRead?.data?.FallbackRouteRead,
                      "status",
                    ),
                    (i) => _successFlags?.includes(i),
                  )
                    ? []
                    : _FallbackRouteRead?.data?.FallbackRouteRead?.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Route Name": item?.displayName ?? "-",
                          "Route Type": item?.type ?? "-",
                          CreatedAt: Moment(item.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                          "Created By": item?.Account?.displayName ?? "-",
                        }),
                      )
                }
                headersData={[
                  "S.No.",
                  "Route Name",
                  "Route Type",
                  "CreatedAt",
                  "UpdatedAt",
                  "Created By",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          <Tabs
            variant="enclosed"
            onChange={_HandleTabChange}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            bg="white"
            isFitted
            overflow="hidden"
            minH={0}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : "none"}
              flexShrink={0}
              minH="auto"
            >
              {_routes.map((u) => (
                <Tab
                  color="white"
                  textDecoration={"line-through"}
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                  p="1rem"
                  key={u}
                >
                  {u}
                </Tab>
              ))}
            </TabList>

            <Flex
              flex={1}
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
              overflow="hidden"
              minH={0}
              width="100%"
            >
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={48}
                rowCount={rowCount}
                paginationMode="server"
                filterMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                onFilterModelChange={_HandleFilterModelChange}
                sortingMode="client"
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                pageSizeOptions={[8, 16, 24, 32]}
                slots={{
                  loadingOverlay: () => (
                    <TableSpinner
                      isLoading={true}
                      isEmpty={false}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noResultsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noRowsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) =>
                  setColumnVisibilityModel(newModel)
                }
                loading={_FallbackRouteRead?.loading}
                disableColumnMenu={false}
                sx={{
                  height: "100%",
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>

        <Modal
          size="md"
          title="Fallback Route Create"
          isOpen={isRouteCreateFormOpen}
          onClose={onRouteCreateFormClose}
        >
          <FallbackRouteUpsert
            type={_routes[tabIndex]}
            isCreateOnly={true}
            isOpen={isRouteCreateFormOpen}
            onClose={onRouteCreateFormClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Fallback Route Update"
          isOpen={isRouteUpdateFormOpen}
          onClose={onRouteUpdateFormClose}
        >
          <FallbackRouteUpsert
            type={_routes[tabIndex]}
            isOpen={isRouteUpdateFormOpen}
            onClose={onRouteUpdateFormClose}
            refetch={refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Fallback Route Delete"
          isOpen={isDeleteModalOpen}
          onClose={onDeleteModalClose}
        >
          <FallbackRouteDelete refetch={refetch} />
        </Modal>
        {/* RouteTerminal is disabled. Uncomment its import, disclosure and dropdown handler to restore it.
        <Modal
          size='3xl'
          title='Fallback Route Terminal'
          isOpen={isRouteTerminalOpen}
          onClose={onRouteTerminalClose}
          isCentered={false}>
          <RouteTerminal refetch={refetch} />
        </Modal>
        */}
      </Flex>
    </>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORTS
 */
export default Index
